/**
 * #987 stage 3 — the project's own formatting settings, so a team gets the same
 * result every time, read from the file's folder and its parents through the
 * drive API as the person editing (it never reads what their drive doesn't show):
 *
 *  - Prettier: the NEAREST `.prettierrc` (JSON or YAML), `.prettierrc.json`,
 *    `.prettierrc.yaml` / `.yml`, or a `package.json` with a `"prettier"` key —
 *    Prettier's own search, minus the JS configs (`prettier.config.js` …), which
 *    would mean running the project's code; one found nearer is reported and the
 *    defaults are used. `overrides` apply by their `files` globs.
 *  - `.editorconfig`: every one from the file up to the first `root = true`,
 *    nearer sections winning — indent style / size, line length, line ending.
 *    Prettier reads it the same way; its `.prettierrc` wins where both set a value.
 *  - Ruff (Python): the nearest folder with `.ruff.toml`, `ruff.toml` or a
 *    `pyproject.toml` that has `[tool.ruff]` (in that order) — Ruff's search.
 *    `line-length`, `indent-width` and the `[format]` table are used.
 *
 * Everything that can't be read or parsed is skipped (the defaults apply), and
 * answers are cached briefly so formatting twice doesn't walk the drive twice.
 */
import { parse as parseYaml } from 'yaml'
import { parse as parseToml } from 'smol-toml'
import { globMatches } from './glob.js'
import type { FormatConfig } from './format-config.js'

export type { FormatConfig } from './format-config.js'

export interface ConfigReader {
  getBreadcrumbs(fileId: string): Promise<Array<{ id: string; name: string }>>
  listChildren(parentId: string, options?: { limit?: number; offset?: number }): Promise<{ files: Array<{ id: string; name: string; isFolder: boolean; contentHash?: string | null; size?: number }> }>
  getFileContent(fileId: string): Promise<Blob>
}


const TTL_MS = 10_000
const MAX_CONFIG_BYTES = 256 * 1024
const LIST_PAGE = 500
const MAX_LIST_PAGES = 10
const PRETTIER_FILES = ['.prettierrc', '.prettierrc.json', '.prettierrc.yaml', '.prettierrc.yml', 'package.json']
const PRETTIER_JS = ['.prettierrc.js', '.prettierrc.cjs', '.prettierrc.mjs', '.prettierrc.ts', 'prettier.config.js', 'prettier.config.cjs', 'prettier.config.mjs', 'prettier.config.ts', '.prettierrc.toml', '.prettierrc.json5']
const RUFF_FILES = ['.ruff.toml', 'ruff.toml', 'pyproject.toml']
const WANTED = new Set([...PRETTIER_FILES, ...PRETTIER_JS, ...RUFF_FILES, '.editorconfig'])

type ListedConfig = { id: string; hash: string | null; size: number }
const listings = new Map<string, { at: number; value: Promise<Map<string, ListedConfig>> }>()
/** Config text by `id:contentHash` — an edited file has a new hash. Bounded: the oldest entries go first. */
const contents = new Map<string, Promise<string | null>>()
const MAX_CACHED_CONTENTS = 64

function configFilesIn(api: ConfigReader, folderId: string) {
  const hit = listings.get(folderId)
  if (hit && Date.now() - hit.at < TTL_MS) return hit.value
  // Page through the folder (the API serves 500 at a time), up to MAX_LIST_PAGES pages, until a page comes back
  // EMPTY: `hasMore` reads false on a page the visibility filter shortened, though rows follow, and a short page
  // looks the same. Accepted, until GET /api/files reports its unfiltered hasMore: one extra (empty-page) request
  // per folder; a run of 500 rows the person can't see ends the listing early; past MAX_LIST_PAGES, a config
  // file isn't found.
  const value = (async () => {
    const out = new Map<string, ListedConfig>()
    for (let page = 0; page < MAX_LIST_PAGES; page++) {
      const { files } = await api.listChildren(folderId, { limit: LIST_PAGE, offset: page * LIST_PAGE })
      if (files.length === 0) break
      for (const f of files) if (!f.isFolder && WANTED.has(f.name)) out.set(f.name, { id: f.id, hash: f.contentHash ?? null, size: f.size ?? 0 })
    }
    return out
  })().catch(() => new Map<string, ListedConfig>())
  listings.set(folderId, { at: Date.now(), value })
  return value
}

function readText(api: ConfigReader, file: ListedConfig): Promise<string | null> {
  if (file.size > MAX_CONFIG_BYTES) return Promise.resolve(null)
  const load = () => api.getFileContent(file.id).then((b) => (b.size > MAX_CONFIG_BYTES ? null : b.text())).catch(() => null)
  if (!file.hash) return load() // nothing to key a cache on
  const key = `${file.id}:${file.hash}`
  let p = contents.get(key)
  if (!p) {
    p = load()
    contents.set(key, p)
    if (contents.size > MAX_CACHED_CONTENTS) contents.delete(contents.keys().next().value!)
  }
  return p
}

/** Tests only. */
export function clearFormatConfigCache() {
  listings.clear()
  contents.clear()
}

function parsePrettierrc(name: string, text: string): Record<string, unknown> | null {
  try {
    if (name === 'package.json') {
      const v = (JSON.parse(text) as { prettier?: unknown }).prettier
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
    }
    // `.prettierrc` is JSON or YAML; YAML is a superset of JSON.
    const v = name.endsWith('.json') ? JSON.parse(text) : parseYaml(text)
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Prettier's options for `relPath` (relative to the config's folder): the base plus every matching override. */
export function resolvePrettierOptions(raw: Record<string, unknown>, relPath: string): Record<string, unknown> {
  const { overrides, $schema: _schema, ...base } = raw
  const out: Record<string, unknown> = { ...base }
  if (Array.isArray(overrides)) {
    for (const o of overrides as Array<{ files?: unknown; excludeFiles?: unknown; options?: unknown }>) {
      const files = typeof o.files === 'string' ? [o.files] : Array.isArray(o.files) ? o.files.filter((f): f is string => typeof f === 'string') : []
      const excl = typeof o.excludeFiles === 'string' ? [o.excludeFiles] : Array.isArray(o.excludeFiles) ? o.excludeFiles.filter((f): f is string => typeof f === 'string') : []
      if (files.some((g) => globMatches(g, relPath)) && !excl.some((g) => globMatches(g, relPath)) && o.options && typeof o.options === 'object') {
        Object.assign(out, o.options)
      }
    }
  }
  return out
}

/** An `.editorconfig`'s sections, in file order: [glob, properties]. */
export function parseEditorconfig(text: string): { root: boolean; sections: Array<[string, Record<string, string>]> } {
  let root = false
  const sections: Array<[string, Record<string, string>]> = []
  let current: Record<string, string> | null = null
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line || line.startsWith('#') || line.startsWith(';')) continue
    const sec = /^\[(.*)\]$/.exec(line)
    if (sec) { current = {}; sections.push([sec[1]!, current]); continue }
    const kv = /^([^=:]+?)\s*[=:]\s*(.*?)\s*$/.exec(line)
    if (!kv) continue
    const key = kv[1]!.toLowerCase()
    const value = kv[2]!.replace(/\s+[#;].*$/, '').toLowerCase()
    if (current) current[key] = value
    else if (key === 'root') root = value === 'true'
  }
  return { root, sections }
}

/** `.editorconfig` properties in Prettier's terms (Prettier's own mapping). */
export function editorconfigToPrettier(props: Record<string, string>): NonNullable<FormatConfig['editorconfig']> {
  const out: NonNullable<FormatConfig['editorconfig']> = {}
  if (props.indent_style === 'tab') out.useTabs = true
  else if (props.indent_style === 'space') out.useTabs = false
  const size = Number(props.indent_size === 'tab' ? props.tab_width : props.indent_size ?? props.tab_width)
  if (Number.isInteger(size) && size > 0) out.tabWidth = size
  const width = Number(props.max_line_length)
  if (Number.isInteger(width) && width > 0) out.printWidth = width
  if (props.end_of_line === 'lf' || props.end_of_line === 'crlf' || props.end_of_line === 'cr') out.endOfLine = props.end_of_line
  return out
}

function ruffSettings(name: string, text: string): Record<string, unknown> | null {
  try {
    const doc = parseToml(text) as Record<string, unknown>
    const table = name === 'pyproject.toml'
      ? ((doc.tool as Record<string, unknown> | undefined)?.ruff as Record<string, unknown> | undefined)
      : doc
    if (!table || typeof table !== 'object') return null
    const out: Record<string, unknown> = {}
    for (const k of ['line-length', 'indent-width', 'format']) if (k in table) out[k] = table[k]
    return out
  } catch {
    return null
  }
}

/** The project's formatting settings for one file (see the top of this file). */
export async function loadFormatConfig(api: ConfigReader, fileId: string, fileName: string): Promise<FormatConfig> {
  const crumbs = await api.getBreadcrumbs(fileId).catch(() => [])
  const folders = crumbs[crumbs.length - 1]?.id === fileId ? crumbs.slice(0, -1) : crumbs
  const config: FormatConfig = { prettier: null, editorconfig: null, ruff: null, unreadable: null }
  let prettierDone = false
  let ruffDone = false
  let editorconfigDone = false
  const editorconfigs: Array<{ relPath: string; text: string }> = []
  // Every folder's listing at once (one round trip of latency, not one per level); then nearest first.
  const listed = folders.map((f) => configFilesIn(api, f.id))
  // `rel` is the file's path relative to the folder being looked at.
  let rel = fileName
  for (let i = folders.length - 1; i >= 0 && !(prettierDone && ruffDone && editorconfigDone); i--) {
    const files = await listed[i]!
    if (!prettierDone) {
      const js = PRETTIER_JS.find((n) => files.has(n))
      for (const name of PRETTIER_FILES) {
        const f = files.get(name)
        if (!f) continue
        const text = await readText(api, f)
        const raw = text == null ? null : parsePrettierrc(name, text)
        if (raw) { config.prettier = resolvePrettierOptions(raw, rel); prettierDone = true; break }
      }
      if (!prettierDone && js) { config.unreadable = js; prettierDone = true }
    }
    if (!ruffDone) {
      for (const name of RUFF_FILES) {
        const f = files.get(name)
        if (!f) continue
        const text = await readText(api, f)
        const settings = text == null ? null : ruffSettings(name, text)
        if (settings) { config.ruff = settings; ruffDone = true; break }
      }
    }
    if (!editorconfigDone) {
      const f = files.get('.editorconfig')
      const text = f ? await readText(api, f) : null
      if (text != null) {
        editorconfigs.push({ relPath: rel, text })
        if (parseEditorconfig(text).root) editorconfigDone = true
      }
    }
    rel = `${folders[i]!.name}/${rel}`
  }
  // Farthest first, so nearer files (and later sections) win.
  const props: Record<string, string> = {}
  for (const { relPath, text } of editorconfigs.reverse()) {
    for (const [glob, p] of parseEditorconfig(text).sections) if (globMatches(glob, relPath)) Object.assign(props, p)
  }
  if (Object.keys(props).length) config.editorconfig = editorconfigToPrettier(props)
  return config
}
